import { useEffect, useState } from "react";
import CreateEditProcessForm from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/create-edit-process-form.tsx";
import {
  cleanAndParseProcess,
  packProcessToOverwrite,
  packProcessToSave,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { fetchProcessDetails } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import {
  useDeleteModelSkillRevision,
  useUpdateModelSkill,
} from "@via/pages/cortex/model-skill/model-skill.api.tsx";
import {
  ModelSkillListItem,
  UpdateModelSkillRequestBody,
  MenuGroupType,
} from "common";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import {
  useCreateModelSkillRevision,
  useDuplicateModelSkill,
} from "@via/pages/cortex/model-garden/model-garden.api";

interface EditModelSkillFormProps {
  modelSkill: ModelSkillListItem;
  setOpen: (open: boolean) => void;
}

const EditModelSkillForm = ({
  modelSkill,
  setOpen,
}: EditModelSkillFormProps) => {
  const [initialData, setInitialData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { mutate: duplicateModelSkill } = useDuplicateModelSkill();

  const {
    mutateAsync: updateModelSkill,
    isPending: isUpdateModelSkillLoading,
    isSuccess: isUpdateModelSkillSuccess,
  } = useUpdateModelSkill();

  const {
    mutate: createProcessRevision,
    isPending: isCreateProcessRevisionLoading,
    isSuccess: isCreateProcessRevisionSuccess,
    data: createProcessRevisionData,
  } = useCreateModelSkillRevision();

  const {
    mutateAsync: deleteModelSkillRevision,
    isPending: isDeleteModelSkillRevisionLoading,
    isSuccess: isDeleteModelSkillRevisionSuccess,
  } = useDeleteModelSkillRevision();

  useEffect(() => {
    if (createProcessRevisionData) {
      const parsed = cleanAndParseProcess(createProcessRevisionData);
      setInitialData(parsed);
      handleChangeRevision(parsed);
    }
  }, [isCreateProcessRevisionSuccess]);

  // Fetch process details on mount using model skill id
  // Note: This assumes the model skill id can be used to fetch the associated process
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Fetch process details using model skill id
        const processDetailsData = await fetchProcessDetails(
          modelSkill.processId.toString()
        );
        const parsed = cleanAndParseProcess(processDetailsData);
        parsed.type = MenuGroupType.MODEL_SKILL.toString();
        setInitialData(parsed);
      } catch (error) {
        console.error("Failed to fetch process details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (modelSkill?.id) {
      fetchData();
    }
  }, [modelSkill]);

  useEffect(() => {
    if (isUpdateModelSkillSuccess) {
      setOpen(false);
    }
  }, [isUpdateModelSkillSuccess, setOpen]);

  useEffect(() => {
    if (isDeleteModelSkillRevisionSuccess) {
      setOpen(false);
    }
  }, [isDeleteModelSkillRevisionSuccess, setOpen]);

  const handleSaveProcess = async (processDraft: any) => {
    try {
      // Transform process data using packProcessToSave
      const processData = packProcessToSave(processDraft);

      await createProcessRevision({
        processId: modelSkill?.processId,
        payload: processData,
      });
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error("Failed to update model skill:", error);
    }
  };

  const handleOverwriteProcess = async (processDraft: any) => {
    try {
      const processData = packProcessToOverwrite(processDraft);

      // Create the model skill update payload
      const payload: UpdateModelSkillRequestBody = {
        process: processData,
      };

      await updateModelSkill({ processId: modelSkill.processId, payload });
    } catch (error) {
      // Error handling is done in the mutation hook
      console.error("Failed to update model skill:", error);
    }
  };

  const handleChangeRevision = (processDraft: any) => {
    const parsed = cleanAndParseProcess(processDraft);
    setInitialData(parsed);
  };

  const handleDuplicateModelSkill = (processId: number) => {
    duplicateModelSkill(processId);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  if (isLoading || !initialData) {
    return (
      <div className="p-4 grid grid-cols-1 gap-6 py-6 sm:grid-cols-3 lg:grid-cols-4">
        <SkeletonLoader count={12} />
      </div>
    );
  }

  return (
    <CreateEditProcessForm
      title="Edit Model Skill"
      data={initialData}
      isCreateProcessRevisionLoading={isCreateProcessRevisionLoading}
      isOverwriteRevisionLoading={isUpdateModelSkillLoading}
      isCreateNewProcessLoading={false}
      isDeleteRevisionLoading={isDeleteModelSkillRevisionLoading}
      onSaveProcess={handleSaveProcess}
      onChangeRevision={handleChangeRevision}
      onOverwriteProcess={handleOverwriteProcess}
      onDuplicateProcess={handleDuplicateModelSkill}
      onDeleteRevision={deleteModelSkillRevision}
      onCancel={handleCancel}
    />
  );
};

export default EditModelSkillForm;
